import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyValue, KeyValueList, Panel } from './Page';
import { ProgressBar } from './Feedback';
import { Chip } from './Chips';
import { useAuth } from '../lib/auth';
import { onboarding as onboardingApi } from '../lib/endpoints';
import { useOnboarding } from '../lib/onboarding';
import { GatewayRole, TenantProvisioningStatus } from '../lib/enums';
import { formatDate, formatNumber, formatRelative } from '../lib/format';
import type { LicenceDto, OnboardingStepDto, TenantOnboardingDto } from '../lib/types';

// ---------------------------------------------------------------------------
// Provisioning
// ---------------------------------------------------------------------------

interface ProvisioningCopy {
  tone: 'info' | 'warn' | 'bad';
  title: string;
  detail: string;
  /** Extra line for the states where iERP has to act rather than the user. */
  footnote?: string;
}

/** null means Ready — nothing to say. */
function provisioningCopy(
  status: TenantProvisioningStatus,
  message: string | null,
): ProvisioningCopy | null {
  switch (status) {
    case TenantProvisioningStatus.Ready:
      return null;
    case TenantProvisioningStatus.Pending:
      return {
        tone: 'info',
        title: 'Finishing your account setup',
        detail: message ?? 'We are preparing your workspace. This page updates by itself.',
      };
    case TenantProvisioningStatus.Registering:
      return {
        tone: 'info',
        title: 'Setting up your iERP AI licence',
        detail: 'This usually takes under a minute.',
        footnote: message ?? undefined,
      };
    case TenantProvisioningStatus.AwaitingLicenceVerification:
      return {
        tone: 'warn',
        title: 'Your iERP AI licence needs verifying',
        detail:
          message ??
          'iERP has emailed a verification link for the processing licence. Documents cannot be submitted until it is confirmed.',
      };
    case TenantProvisioningStatus.Failed:
      return {
        tone: 'bad',
        title: 'Licence setup did not complete',
        detail: message ?? 'The iERP AI licence could not be issued.',
        footnote: 'Support has been notified and the gateway will keep retrying.',
      };
    case TenantProvisioningStatus.Suspended:
      return {
        tone: 'bad',
        title: 'This account is suspended',
        detail:
          message ??
          'iERP has suspended processing for this account. Documents cannot be submitted until it is reinstated.',
        footnote: 'Contact iERP to have the account reinstated.',
      };
    default:
      return null;
  }
}

/** Seconds between resends. Matches the server's per-tenant rate limit. */
const RESEND_COOLDOWN_SECONDS = 60;

export function ProvisioningBanner({
  status,
  message,
  polling = false,
  pollExhausted = false,
  onRefresh,
  onResend,
}: {
  status: TenantProvisioningStatus;
  message: string | null;
  polling?: boolean;
  pollExhausted?: boolean;
  onRefresh?: () => void;
  /** Requests a fresh verification email and resolves to the confirmation to show. Admin only. */
  onResend?: () => Promise<string>;
}): JSX.Element | null {
  const [resendBusy, setResendBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const copy = provisioningCopy(status, message);
  if (!copy) return null;

  const resend = async () => {
    if (!onResend || resendBusy || resendCooldown > 0) return;
    setResendBusy(true);
    setResendError(null);
    setResendNote(null);
    try {
      const confirmation = await onResend();
      setResendNote(confirmation);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (cause) {
      setResendError(
        cause instanceof Error ? cause.message : 'The verification email could not be resent.',
      );
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div
      className={`banner banner-${copy.tone} provisioning-banner`}
      role={copy.tone === 'bad' ? 'alert' : 'status'}
      aria-live="polite"
    >
      <div className="banner-body">
        <p className="banner-title">
          {/* Decorative: the banner itself is the live region, so no nested status role. */}
          {polling ? <span className="spinner-ring" aria-hidden="true" /> : null}
          {copy.title}
        </p>
        <p className="banner-detail">{copy.detail}</p>
        {copy.footnote ? <p className="banner-detail">{copy.footnote}</p> : null}
        {resendNote ? <p className="banner-detail">{resendNote}</p> : null}
        {resendError ? <p className="banner-error">{resendError}</p> : null}
        {pollExhausted ? (
          <p className="banner-detail">
            This is taking longer than usual. Refresh to check again.
          </p>
        ) : null}
      </div>
      <div className="banner-actions">
        {onResend ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void resend()}
            disabled={resendBusy || resendCooldown > 0}
          >
            {resendBusy
              ? 'Sending…'
              : resendCooldown > 0
                ? `Resend email in ${resendCooldown}s`
                : 'Resend verification email'}
          </button>
        ) : null}
        {onRefresh ? (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onRefresh}>
            Check again
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Licence and trial quota
// ---------------------------------------------------------------------------

/** Days of licence left below which the expiry date is worth showing. */
const EXPIRY_NOTICE_DAYS = 15;

/** Today as the yyyy-MM-dd the licence dates arrive in, in the viewer's own calendar. */
function todayIsoDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function LicenceBanner({ licence }: { licence: LicenceDto }): JSX.Element | null {
  const limit = licence.monthlyLimit;
  const remaining = licence.remainingThisMonth;
  const showMeter = licence.isTrial && limit !== null && limit > 0 && remaining !== null;
  const expiringSoon =
    licence.daysRemaining !== null && licence.daysRemaining < EXPIRY_NOTICE_DAYS;
  // Past its end date a licence reports 0 days remaining - the same as its last day - through the
  // grace period and after it. Only the end date itself tells "ends today" from "has ended".
  const lapsed =
    expiringSoon && !!licence.subscriptionEndDate && licence.subscriptionEndDate.slice(0, 10) < todayIsoDate();
  const renewable = !!licence.packageCode && !licence.isTrial;

  if (!licence.quotaExhausted && !licence.isTrial && !expiringSoon) return null;

  const usedFraction =
    showMeter && limit !== null && remaining !== null
      ? Math.max(0, Math.min(1, remaining / limit))
      : null;

  const tone = licence.quotaExhausted ? 'info' : expiringSoon ? 'warn' : 'neutral';

  const title = licence.quotaExhausted
    ? 'You have used this month’s page allowance'
    : lapsed
      ? `Your licence ended ${formatDate(licence.subscriptionEndDate)}`
      : expiringSoon
        ? `Your licence ends ${licence.daysRemaining === 0 ? 'today' : `in ${licence.daysRemaining} day${licence.daysRemaining === 1 ? '' : 's'}`}`
        : 'Trial licence';

  const detail = licence.quotaExhausted
    ? 'Contact iERP to upgrade and keep processing documents. Everything already posted stays exactly as it is.'
    : lapsed
      ? renewable
        ? 'Renew below to start a new term and keep document processing available.'
        : 'Contact iERP to renew and keep document processing available.'
      : expiringSoon
        ? renewable
          ? 'Renew below to buy the next term before it lapses, otherwise document processing stops.'
          : 'Contact iERP to renew before it lapses, otherwise document processing stops.'
        : 'You are on a trial licence. Contact iERP when you are ready to upgrade.';

  // Most trial tiers are literally called "Trial"; do not badge the same word twice.
  const showTier = licence.tier !== null && !(licence.isTrial && /trial/i.test(licence.tier));

  return (
    <div className={`banner banner-${tone} licence-banner`}>
      <div className="banner-body">
        <p className="banner-title">
          {title}
          {licence.isTrial ? <Chip tone="info">Trial</Chip> : null}
          {showTier ? <Chip tone="neutral">{licence.tier}</Chip> : null}
        </p>
        <p className="banner-detail">{detail}</p>

        {showMeter && limit !== null && remaining !== null ? (
          <div className="licence-meter">
            <ProgressBar
              value={(usedFraction ?? 0) * 100}
              tone={remaining === 0 ? 'bad' : (usedFraction ?? 1) <= 0.2 ? 'bad' : 'good'}
              label={`${remaining} of ${limit} pages left this month`}
            />
            <p className="licence-meter-caption">
              {formatNumber(remaining)} of {formatNumber(limit)} pages left this month
              {licence.remainingToday !== null && licence.dailyLimit !== null
                ? ` · ${formatNumber(licence.remainingToday)} of ${formatNumber(licence.dailyLimit)} left today`
                : ''}
            </p>
          </div>
        ) : null}

        {licence.subscriptionEndDate && expiringSoon && !lapsed ? (
          <p className="banner-detail">Licence expires {formatDate(licence.subscriptionEndDate)}.</p>
        ) : null}
        {licence.message ? <p className="banner-detail">{licence.message}</p> : null}

        {expiringSoon && renewable && licence.packageCode ? (
          <Link
            className="btn btn-primary btn-sm"
            to={`/settings?renewPlan=${encodeURIComponent(licence.packageCode)}`}
          >
            Renew now
          </Link>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan and usage (always on)
// ---------------------------------------------------------------------------

/**
 * A customer paying a monthly package fee should be able to see what the package includes and how
 * much of it is left without waiting for something to go wrong. LicenceBanner only appears for a
 * trial, an exhausted quota or an imminent expiry, so these two surfaces carry the same numbers
 * permanently: the panel on Settings, the one-liner in the dashboard's onboarding area.
 *
 * Only what LicenceDto actually carries is shown. The backend does not expose an AI Power code
 * separately from the licence tier, so nothing here invents one.
 */

interface Allowance {
  limit: number;
  remaining: number;
  used: number;
  usedPercent: number;
}

/** null when the tenant has no such cap, or the server did not say what is left. */
function allowanceOf(limit: number | null, remaining: number | null): Allowance | null {
  if (limit === null || limit <= 0 || remaining === null) return null;
  const clamped = Math.max(0, Math.min(limit, remaining));
  return {
    limit,
    remaining: clamped,
    used: limit - clamped,
    usedPercent: ((limit - clamped) / limit) * 100,
  };
}

function allowanceTone(allowance: Allowance): 'good' | 'bad' {
  // Same threshold the trial meter uses: the last fifth of the allowance reads as a problem.
  return allowance.remaining === 0 || allowance.remaining / allowance.limit <= 0.2 ? 'bad' : 'good';
}

/** "820 of 1,000 pages left", or an honest statement that there is no such cap. */
function describeAllowance(allowance: Allowance | null, limit: number | null, noCap: string): string {
  if (allowance) {
    return `${formatNumber(allowance.remaining)} of ${formatNumber(allowance.limit)} left`;
  }
  // A limit with no remaining count still tells the customer what they bought.
  if (limit !== null && limit > 0) return `${formatNumber(limit)} included`;
  return noCap;
}

/** The package as the licence describes it: tier is the name, package code the identifier. */
function planName(licence: LicenceDto): string | null {
  const tier = licence.tier?.trim();
  if (tier) return tier;
  const code = licence.packageCode?.trim();
  return code ? code : null;
}

/** Shown when the package code adds something the tier name does not already say. */
function planCode(licence: LicenceDto): string | null {
  const code = licence.packageCode?.trim();
  if (!code) return null;
  const tier = licence.tier?.trim();
  if (!tier || tier.toLowerCase() === code.toLowerCase()) return null;
  return code;
}

/**
 * The registration server's own status word, when it says more than the Active chip already does.
 * It is free text from upstream, so it is shown rather than interpreted.
 */
function extraStatus(licence: LicenceDto): string | null {
  const status = licence.status?.trim();
  if (!status) return null;
  const implied = licence.active ? 'active' : 'inactive';
  return status.toLowerCase() === implied ? null : status;
}

function describeDaysRemaining(days: number | null): string | null {
  if (days === null) return null;
  if (days <= 0) return 'today';
  return `${formatNumber(days)} day${days === 1 ? '' : 's'} left`;
}

export function PlanUsagePanel({ licence }: { licence: LicenceDto }): JSX.Element {
  const monthly = allowanceOf(licence.monthlyLimit, licence.remainingThisMonth);
  const daily = allowanceOf(licence.dailyLimit, licence.remainingToday);
  const days = describeDaysRemaining(licence.daysRemaining);
  const name = planName(licence);
  const code = planCode(licence);
  const status = extraStatus(licence);
  // The licence carries a subscription end date; for a monthly package that is the renewal date.
  const endLabel = licence.isTrial ? 'Trial ends' : 'Renews';

  return (
    <Panel
      title="Plan and usage"
      description="Your iERP package, the page allowance it carries, and how much of it is left."
    >
      <div className="plan-usage-head">
        {name ? <Chip tone="neutral">{name}</Chip> : null}
        {licence.isTrial ? <Chip tone="info">Trial</Chip> : null}
        <Chip tone={licence.active ? 'good' : 'bad'}>
          {licence.active ? 'Active' : 'Not active'}
        </Chip>
        {status ? <span className="plan-usage-status">{status}</span> : null}
      </div>

      {monthly ? (
        <div className="licence-meter plan-usage-meter">
          <ProgressBar
            value={monthly.usedPercent}
            tone={allowanceTone(monthly)}
            label={`${formatNumber(monthly.used)} of ${formatNumber(monthly.limit)} pages used this month`}
          />
          <p className="licence-meter-caption">
            {formatNumber(monthly.used)} of {formatNumber(monthly.limit)} pages used this month ·{' '}
            {formatNumber(monthly.remaining)} left
          </p>
        </div>
      ) : null}

      <KeyValueList columns={3}>
        <KeyValue label="Package">
          {name ?? 'Not set'}
          {code ? <span className="cell-sub mono">{code}</span> : null}
        </KeyValue>
        <KeyValue label="Pages this month">
          {describeAllowance(monthly, licence.monthlyLimit, 'No monthly page allowance')}
        </KeyValue>
        <KeyValue label="Pages today">
          {describeAllowance(daily, licence.dailyLimit, 'No daily cap')}
        </KeyValue>
        <KeyValue label={endLabel}>
          {licence.subscriptionEndDate ? formatDate(licence.subscriptionEndDate) : (days ?? '—')}
          {licence.subscriptionEndDate && days ? <span className="cell-sub">{days}</span> : null}
        </KeyValue>
        <KeyValue label="Started">
          {licence.subscriptionStartDate ? formatDate(licence.subscriptionStartDate) : '—'}
        </KeyValue>
        <KeyValue label="Checked">
          {licence.checkedUtc ? formatRelative(licence.checkedUtc) : '—'}
        </KeyValue>
      </KeyValueList>

      {licence.message ? <p className="panel-footnote">{licence.message}</p> : null}
    </Panel>
  );
}

/** The same facts as one line, for the dashboard. */
function planUsageFacts(licence: LicenceDto): string[] {
  const monthly = allowanceOf(licence.monthlyLimit, licence.remainingThisMonth);
  const daily = allowanceOf(licence.dailyLimit, licence.remainingToday);
  const days = describeDaysRemaining(licence.daysRemaining);

  const facts = [planName(licence) ?? 'Package not set'];

  facts.push(
    monthly
      ? `${formatNumber(monthly.remaining)} of ${formatNumber(monthly.limit)} pages left this month`
      : licence.monthlyLimit !== null && licence.monthlyLimit > 0
        ? `${formatNumber(licence.monthlyLimit)} pages a month`
        : 'no monthly page allowance',
  );

  if (daily) {
    facts.push(`${formatNumber(daily.remaining)} of ${formatNumber(daily.limit)} left today`);
  }

  const verb = licence.isTrial ? 'trial ends' : 'renews';
  if (licence.subscriptionEndDate) {
    facts.push(
      `${verb} ${formatDate(licence.subscriptionEndDate)}${days ? ` · ${days}` : ''}`,
    );
  } else if (days) {
    facts.push(days);
  }

  return facts;
}

export function PlanUsageLine({ licence }: { licence: LicenceDto }): JSX.Element {
  return (
    <div className="plan-usage-line">
      <span className="plan-usage-label">Plan and usage</span>
      <span className="plan-usage-facts">{planUsageFacts(licence).join(' · ')}</span>
      <Link className="btn btn-ghost btn-sm" to="/settings">
        Plan details
      </Link>
    </div>
  );
}

/** Plan and usage for a page that has no onboarding state of its own, e.g. Settings. */
export function PlanUsageCard(): JSX.Element | null {
  const { data } = useOnboarding();
  if (!data) return null;
  return <PlanUsagePanel licence={data.licence} />;
}

// ---------------------------------------------------------------------------
// First-run checklist
// ---------------------------------------------------------------------------

function ChecklistItem({ step }: { step: OnboardingStepDto }): JSX.Element {
  const state = step.complete ? 'done' : step.blocked ? 'blocked' : 'todo';
  return (
    <li className={`checklist-item checklist-${state}`}>
      <span className="checklist-mark" aria-hidden="true">
        {step.complete ? '✓' : step.blocked ? '–' : '○'}
      </span>
      <div className="checklist-body">
        <p className="checklist-title">
          {step.title}
          <span className="visually-hidden">
            {step.complete ? ' — complete' : step.blocked ? ' — blocked' : ' — still to do'}
          </span>
          {step.blocked && !step.complete ? <Chip tone="neutral">Blocked</Chip> : null}
        </p>
        <p className="checklist-description">{step.description}</p>
      </div>
      <div className="checklist-action">
        {step.complete ? (
          <span className="checklist-state">Done</span>
        ) : step.blocked ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled
            title="Finish the earlier steps first."
          >
            {step.actionLabel ?? 'Not available yet'}
          </button>
        ) : step.actionRoute && step.actionLabel ? (
          <Link className="btn btn-primary btn-sm" to={step.actionRoute}>
            {step.actionLabel}
          </Link>
        ) : null}
      </div>
    </li>
  );
}

/** Renders nothing once every step is complete. */
export function OnboardingChecklist({ steps }: { steps: OnboardingStepDto[] }): JSX.Element | null {
  if (steps.length === 0) return null;
  const complete = steps.filter((step) => step.complete).length;
  if (complete === steps.length) return null;

  return (
    <Panel
      title="Finish setting up"
      description={`${complete} of ${steps.length} steps complete. Work through these to start posting to QuickBooks.`}
      className="checklist-panel"
    >
      <ProgressBar
        value={(complete / steps.length) * 100}
        tone="info"
        label={`Setup progress: ${complete} of ${steps.length} steps complete`}
      />
      <ol className="checklist">
        {steps.map((step) => (
          <ChecklistItem key={step.key} step={step} />
        ))}
      </ol>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Why uploads are refused, or null when they are allowed. Used by the Documents dropzone so the
 * gate always explains itself instead of just going grey.
 */
export function submissionBlockedReason(state: TenantOnboardingDto): string | null {
  if (state.canSubmitDocuments) return null;

  if (state.provisioningStatus !== TenantProvisioningStatus.Ready) {
    const copy = provisioningCopy(state.provisioningStatus, state.provisioningMessage);
    if (copy) return `${copy.title}. ${copy.detail}`;
  }

  if (state.licence.quotaExhausted) {
    return 'This month’s page allowance is used up. Contact iERP to upgrade, then uploads will resume.';
  }

  if (!state.licence.active) {
    return (
      state.licence.message ??
      'The iERP AI licence for this account is not active, so documents cannot be processed.'
    );
  }

  return state.provisioningMessage ?? 'Document submission is not available for this account yet.';
}

/**
 * Provisioning strip, trial/licence banner, first-run checklist and the always-on plan line, in the
 * order the Dashboard wants them. Reads the shared onboarding cache, so mounting this costs no extra
 * request.
 */
export function OnboardingSummary(): JSX.Element | null {
  const { data, polling, pollExhausted, reload } = useOnboarding();
  const { user } = useAuth();

  const handleResend = useCallback(async () => {
    const response = await onboardingApi.resendVerification();
    return response.message;
  }, []);

  if (!data) return null;

  // Only an administrator may ask the Registration Server for a fresh email, and only while the
  // registration is actually waiting on one.
  const canResend =
    data.provisioningStatus === TenantProvisioningStatus.AwaitingLicenceVerification &&
    (user?.role ?? GatewayRole.Auditor) >= GatewayRole.Admin;

  return (
    <>
      <ProvisioningBanner
        status={data.provisioningStatus}
        message={data.provisioningMessage}
        polling={polling}
        pollExhausted={pollExhausted}
        onRefresh={reload}
        onResend={canResend ? handleResend : undefined}
      />
      <OnboardingChecklist steps={data.steps} />
      <LicenceBanner licence={data.licence} />
      <PlanUsageLine licence={data.licence} />
    </>
  );
}
